import React, { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import app, { auth } from "../firebaseConfig";
import { resolveEmployeeStatus } from "../utils/employeeStatus";
import {
  buildCompanyRestrictionMessage,
  deriveCompanyAccessState,
  startCompanyAccessMonitor,
} from "../utils/companyAccess";

/**
 * Wrapper for protected routes.
 * Ensures user is authenticated and optionally checks their accountType.
 *
 * Notes:
 * - Reads user profile from Firestore "users/{uid}" (lowercase "users").
 * - allowedRoles comparison is case-insensitive.
 * - If the Firestore user document is missing, we treat role as "unknown" and
 *   will redirect to /unauthorized when allowedRoles is provided.
 */
export default function ProtectedRoute({ children, allowedRoles = [] }) {
  const firestore = getFirestore(app);
  const [authState, setAuthState] = useState({
    loading: true,
    user: null,
    role: null,
    error: null,
    userData: null,
  });
  const [restrictedReason, setRestrictedReason] = useState("");

  useEffect(() => {
    let mounted = true;

    // Check if Firebase is properly initialized
    if (!app || !auth) {
      console.error("Firebase is not properly initialized. Please check your .env file.");
      if (mounted) {
        setAuthState({
          loading: false,
          user: null,
          role: null,
          error: new Error("Firebase configuration is missing. Please contact support.")
        });
      }
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!mounted) return;
      if (!user) {
        setAuthState({ loading: false, user: null, role: null, error: null });
        return;
      }

      try {
        const uid = user.uid;
        const userDocRef = doc(firestore, "users", uid);
        const userSnap = await getDoc(userDocRef);

        const role = userSnap.exists()
        ? (userSnap.data().accountType || userSnap.data().role || "unknown")
        : "unknown";


        if (mounted) {
          setAuthState({ loading: false, user, role, userData: userSnap.exists() ? userSnap.data() : null, error: null });
        }
      } catch (err) {
        // Log and set an error so UI can respond if needed
        // eslint-disable-next-line no-console
        console.error("ProtectedRoute: error fetching user profile:", err);
        if (mounted) {
          setAuthState({ loading: false, user, role: "unknown", userData: null, error: err });
        }
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [firestore]);

  const allowedLower = useMemo(
    () => allowedRoles.map((r) => String(r).toLowerCase()),
    [allowedRoles]
  );
  const userRoleLower = String(authState.role || "unknown").toLowerCase();
  const employeeStatus = resolveEmployeeStatus(authState.userData);
  const needsCompanyAccess = allowedLower.includes("company");

  useEffect(() => {
    const userRole = String(authState.role || "unknown").toLowerCase();
    const shouldCheckCompany =
      !!authState.user &&
      ["company", "employee"].includes(userRole) &&
      (allowedLower.includes("company") || allowedLower.includes("employee"));

    if (!shouldCheckCompany) {
      return undefined;
    }

    const companyKey =
      authState.userData?.companyName ||
      authState.userData?.company ||
      authState.userData?.company_key;

    if (!companyKey) {
      return undefined;
    }

    let cancelled = false;
    const handleRestriction = (reason) => {
      if (cancelled) return;
      setRestrictedReason(reason);
    };

    const checkAccess = async () => {
      try {
        const candidates = Array.from(
          new Set(
            [companyKey, String(companyKey || "").replace(/\//g, "-")].filter(Boolean)
          )
        );
        let permissions = null;
        for (const candidate of candidates) {
          const companySnap = await getDoc(doc(firestore, "companies", candidate));
          if (!companySnap.exists()) continue;
          const subscriptionSnap = await getDoc(
            doc(firestore, "companies", candidate, "subscriptionSnapshots", "current")
          );
          permissions = deriveCompanyAccessState({
            ...companySnap.data(),
            ...(subscriptionSnap.exists() ? subscriptionSnap.data() : {}),
          });
          break;
        }
        if (!permissions) {
          return {
            restricted: true,
            reason:
              "Your company has not been set up for subscription access. Please contact support.",
          };
        }
        return {
          restricted: !permissions.can_operate,
          reason: buildCompanyRestrictionMessage(permissions),
        };
      } catch (_error) {
        return {
          restricted: true,
          reason: "Unable to verify company access right now. Please try again.",
        };
      }
    };

    const checkNow = async () => {
      const result = await checkAccess();
      if (cancelled || !result) return;
      if (result.restricted) {
        handleRestriction(result.reason);
      } else {
        setRestrictedReason("");
      }
    };

    checkNow();

    const stopMonitor = startCompanyAccessMonitor({
      monitorKey: authState.user.uid,
      checkAccess,
      onRestricted: (reason) => {
        handleRestriction(reason);
      },
    });

    return () => {
      cancelled = true;
      stopMonitor();
    };
  }, [authState.role, authState.user, authState.userData, allowedLower, firestore]);

  // Still loading authentication state
  if (authState.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600 text-lg">Checking authentication...</p>
      </div>
    );
  }

  if (restrictedReason) {
    return (
      <Navigate
        to={`/access-restricted?reason=${encodeURIComponent(
          restrictedReason
        )}&returnTo=${encodeURIComponent(window.location.pathname)}`}
        replace
      />
    );
  }

  // If there's an error (e.g., Firebase not initialized)
  if (authState.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Configuration Error</h1>
          <p className="text-gray-600 mb-4">
            The application is not properly configured. Please ensure all environment variables are set.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Error: {authState.error.message}
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // If not logged in
  if (!authState.user) {
    return <Navigate to="/login" replace />;
  }

  // If allowedRoles provided, perform case-insensitive check
  if (allowedRoles.length > 0) {
    if (!allowedLower.includes(userRoleLower)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  if (userRoleLower === "employee") {
    if (employeeStatus === "suspended") {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-amber-50 px-4">
          <div className="max-w-lg bg-white rounded-xl shadow-md border border-amber-100 p-6 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Account suspended</h2>
            <p className="text-gray-600 text-sm">
              Your account access has been temporarily suspended. Please contact your company admin for assistance.
            </p>
            <button
              onClick={() => auth.signOut()}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      );
    }

    if (needsCompanyAccess && employeeStatus !== "approved") {
      return <Navigate to="/employee-dashboard" replace />;
    }
  }

  // Authenticated and allowed
  return children;
}
